import { useState, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import api from "@/lib/api";
import BrandName from "@/components/BrandName";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Building2, Sparkles, Search, Plus, Lock,
  Clapperboard, Briefcase, ChevronRight,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import AssociationAutocomplete from "@/components/AssociationAutocomplete";

// Path chip visual config.
const PATH_CFG = {
  creative:   { icon: Clapperboard, accent: "#F5B82E", tag: "CREW · CRAFT · AGENCY" },
  managerial: { icon: Briefcase,    accent: "#A78BFA", tag: "OFFICE · ACCOUNTS · SUPPORT" },
};

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledAssociation = searchParams.get("association_id") || "";

  const [paths, setPaths] = useState([]);           // full onboard-paths tree
  const [form, setForm] = useState({
    // Wizard fields
    path:            "creative",
    department_key:  "",
    designation_key: "",
    association_id:  prefilledAssociation || "",
    not_a_member:    false,
    membership_number: "",
    // Company attachment (Managerial: required; Creative: optional via freelance toggle)
    is_freelance:           true,   // Creative default = freelance ON; Managerial forces OFF
    staff_company_id:       "",
    staff_company_label:    "",
    staff_new_company_name: "",
    // Personal (bottom of form)
    name: "", email: "", password: "", phone: "",
  });
  const [busy, setBusy] = useState(false);
  const [orgMatch, setOrgMatch] = useState(null);

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Fetch onboard-paths tree once
  useEffect(() => {
    api.get("/meta/onboard-paths")
      .then(({ data }) => setPaths(data.paths || []))
      .catch(() => setPaths([]));
  }, []);

  // Domain-match banner (email → suggested org).
  useEffect(() => {
    const email = form.email.trim();
    if (!email.includes("@")) { setOrgMatch(null); return; }
    const t = setTimeout(() => {
      api.get(`/orgs/discover-by-email?email=${encodeURIComponent(email)}`)
        .then(({ data }) => setOrgMatch(data.match))
        .catch(() => setOrgMatch(null));
    }, 350);
    return () => clearTimeout(t);
  }, [form.email]);

  const activePath = useMemo(
    () => paths.find((p) => p.key === form.path),
    [paths, form.path],
  );
  const activeDept = useMemo(
    () => activePath?.departments.find((d) => d.key === form.department_key),
    [activePath, form.department_key],
  );

  // Reset dept + designation + association + company whenever the path changes.
  const setPath = (nextPath) => {
    const nextPathCfg = paths.find((p) => p.key === nextPath);
    setForm((p) => ({
      ...p,
      path:            nextPath,
      department_key:  "",
      designation_key: "",
      association_id:  "",
      not_a_member:    nextPath === "managerial",
      membership_number: "",
      // Managerial always attaches to an org (is_freelance=false).
      // Creative defaults to freelance ON per path config.
      is_freelance:           nextPathCfg?.freelance_default ?? (nextPath !== "managerial"),
      staff_company_id:       "",
      staff_company_label:    "",
      staff_new_company_name: "",
    }));
  };
  // Reset designation + union whenever department changes.
  const setDept = (nextDept) => {
    setForm((p) => ({
      ...p,
      department_key:  nextDept,
      designation_key: "",
      association_id:  "",
      not_a_member:    p.path === "managerial",
      membership_number: "",
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.department_key)  return toast.error("Please pick a Department.");
    if (!form.designation_key) return toast.error("Please pick a Designation.");
    // Union required on Creative when the dept has union coverage AND user hasn't opted out.
    const requiresUnion = form.path === "creative"
      && activeDept?.role_keys?.length > 0
      && !form.not_a_member;
    if (requiresUnion && !form.association_id) {
      return toast.error("Pick a Trade Association or tick 'Not a member'.");
    }
    // Company: required on Managerial; optional on Creative unless freelance is unchecked.
    const requiresCompany = form.path === "managerial"
      || (form.path === "creative" && !form.is_freelance);
    if (requiresCompany
        && !form.staff_company_id
        && !form.staff_new_company_name.trim()) {
      return toast.error(
        form.path === "managerial"
          ? "Managerial staff must attach to a company. Pick one or add a new company."
          : "Pick your studio / agency or tick 'I'm freelance'."
      );
    }

    setBusy(true);
    try {
      const payload = {
        name:     form.name,
        email:    form.email,
        password: form.password,
        phone:    form.phone,
        account_type:    "crew",
        path:            form.path,
        department_key:  form.department_key,
        designation_key: form.designation_key,
      };
      if (form.path === "creative" && activeDept?.role_keys?.length > 0) {
        payload.association_id =
          form.not_a_member ? "not_registered" : form.association_id;
        if (!form.not_a_member) {
          payload.membership_number = form.membership_number;
        }
      } else {
        // Managerial or Agency-Creative — no film union.
        payload.association_id = "not_registered";
      }
      // Company attachment (Managerial or non-freelance Creative)
      if (requiresCompany) {
        if (form.staff_company_id) {
          payload.staff_company_id = form.staff_company_id;
        } else if (form.staff_new_company_name.trim()) {
          payload.staff_new_company_name = form.staff_new_company_name.trim();
        }
      }
      const res = await register(payload);
      if (res?.org_locked) {
        toast.success("Profile created. Your company is pending verification.");
        nav("/dashboard");
      } else {
        toast.success("Credentials verified. Welcome to CALL SHEET.");
        nav("/icard");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-2xl">
        <Link to="/" className="block mb-8 text-center" data-testid="register-brand">
          <BrandName variant="gold" size="lg" withClapper />
        </Link>
        <div className="cs-card rounded-2xl p-8 cs-grain">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#F5B82E] mb-3">Step 1 / 1 · Verification</div>
          <h1 className="font-display text-3xl uppercase tracking-tight">Verify Industry Credentials</h1>

          <form onSubmit={submit} className="mt-6 space-y-5">
            {/* Prefilled union banner (from /associations claim link) */}
            {prefilledAssociation && (
              <div className="rounded-xl border border-fuchsia-500/40 bg-fuchsia-500/10 p-4 flex items-center gap-3"
                data-testid="reg-prefilled-banner">
                <Sparkles size={14} className="text-fuchsia-300 shrink-0" />
                <div className="text-xs text-fuchsia-100 leading-relaxed">
                  Claiming your <b className="uppercase">{prefilledAssociation}</b> digital membership.
                  <Link to="/associations" className="ml-2 underline decoration-dotted text-fuchsia-300 hover:text-white"
                    data-testid="reg-prefilled-change">
                    Wrong union? Change
                  </Link>
                </div>
              </div>
            )}

            {/* Row 1 — Two path chips */}
            <Field label="Are you a Creative or Support Staff?" full>
              <div className="grid sm:grid-cols-2 gap-3">
                {paths.map((p) => {
                  const cfg = PATH_CFG[p.key] || PATH_CFG.creative;
                  const IconEl = cfg.icon;
                  const active = form.path === p.key;
                  return (
                    <button key={p.key} type="button"
                      onClick={() => setPath(p.key)}
                      data-testid={`reg-path-${p.key}`}
                      className={`text-left rounded-xl border p-3.5 transition ${
                        active
                          ? "border-white/40 bg-white/[0.05]"
                          : "border-white/10 hover:border-white/25"
                      }`}
                      style={active ? { borderColor: cfg.accent, background: `${cfg.accent}12` } : {}}>
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                             style={{ background: `${cfg.accent}22`, color: cfg.accent }}>
                          <IconEl size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[9px] tracking-[0.28em] uppercase" style={{ color: cfg.accent }}>
                            {cfg.tag}
                          </div>
                          <div className="text-sm font-semibold text-white truncate">{p.label}</div>
                        </div>
                        {active && <ChevronRight size={14} className="text-white/40" />}
                      </div>
                      <div className="mt-2 text-[11px] text-white/60 leading-relaxed">{p.blurb}</div>
                    </button>
                  );
                })}
              </div>
            </Field>

            {/* Row 2 — Department (placeholder adapts per path) */}
            <Field label="Department" full>
              <Select value={form.department_key} onValueChange={setDept}>
                <SelectTrigger className="bg-black/40 border-white/15 h-11" data-testid="reg-department">
                  <SelectValue placeholder={activePath?.dept_placeholder || "Pick your department"} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {(activePath?.departments || []).map((d) => (
                    <SelectItem key={d.key} value={d.key} data-testid={`reg-department-opt-${d.key}`}>
                      {d.label}
                      {d.blurb && (
                        <span className="ml-2 text-[10px] text-white/40 uppercase tracking-wider">
                          · {d.blurb}
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            {/* Row 3 — Trade Association (only Creative path + dept with role_keys) */}
            {form.path === "creative" && activeDept && activeDept.role_keys?.length > 0 && (
              <Field label="Trade Association / Union" full>
                <AssociationAutocomplete
                  value={form.not_a_member ? "not_registered" : form.association_id}
                  departmentKey={form.department_key}
                  onPick={({ id }) =>
                    setForm((p) => ({
                      ...p,
                      association_id: id === "not_registered" ? "" : id,
                      not_a_member:   id === "not_registered",
                      membership_number: id === "not_registered" ? "" : p.membership_number,
                    }))
                  }
                  testidPrefix="reg-association"
                />
                <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-white/45">
                  Only the {activeDept.label.toLowerCase()} bodies from our 275-union registry are shown.
                </div>
              </Field>
            )}

            {/* Row 4 — Non-member toggle + membership number (below union) */}
            {form.path === "creative" && activeDept && activeDept.role_keys?.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-2 h-11 px-3 rounded-md border border-white/12 bg-black/30"
                     data-testid="reg-not-member-row">
                  <Checkbox
                    id="not_a_member"
                    checked={form.not_a_member}
                    onCheckedChange={(v) =>
                      setForm((p) => ({
                        ...p,
                        not_a_member: !!v,
                        association_id: v ? "" : p.association_id,
                        membership_number: v ? "" : p.membership_number,
                      }))
                    }
                    data-testid="reg-not-member-checkbox"
                  />
                  <label htmlFor="not_a_member" className="text-[11px] text-white/75 leading-tight cursor-pointer">
                    Not a union member — still claiming this designation
                  </label>
                </div>
                <div>
                  <Input
                    value={form.membership_number}
                    onChange={(e) => upd("membership_number", e.target.value)}
                    placeholder="Membership Number (leave blank if none)"
                    disabled={form.not_a_member}
                    className="bg-black/40 border-white/15 h-11 disabled:opacity-40"
                    data-testid="reg-membership"
                  />
                </div>
              </div>
            )}

            {/* Row 5 — Designation (small dropdown of sub-roles inside the dept) */}
            {activeDept && (
              <Field label="Designation" full>
                <Select value={form.designation_key} onValueChange={(v) => upd("designation_key", v)}>
                  <SelectTrigger className="bg-black/40 border-white/15 h-11" data-testid="reg-designation-key">
                    <SelectValue placeholder={activePath?.designation_placeholder || "Pick your designation"} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {activeDept.sub_roles.map((s) => (
                      <SelectItem key={s.key} value={s.key} data-testid={`reg-designation-opt-${s.key}`}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}

            {/* Row 6 — Company Attachment (Managerial: required; Creative: optional via freelance toggle) */}
            {activeDept && (
              <Field label={activePath?.company_prompt || "Company"} full>
                {form.path === "creative" && (
                  <div className="flex items-center gap-2 mb-3 h-10 px-3 rounded-md border border-white/12 bg-black/30"
                       data-testid="reg-freelance-row">
                    <Checkbox
                      id="is_freelance"
                      checked={form.is_freelance}
                      onCheckedChange={(v) =>
                        setForm((p) => ({
                          ...p,
                          is_freelance: !!v,
                          staff_company_id: v ? "" : p.staff_company_id,
                          staff_company_label: v ? "" : p.staff_company_label,
                          staff_new_company_name: v ? "" : p.staff_new_company_name,
                        }))
                      }
                      data-testid="reg-freelance-checkbox"
                    />
                    <label htmlFor="is_freelance" className="text-[11px] text-white/75 leading-tight cursor-pointer">
                      {activePath?.freelance_label || "I'm freelance — not attached to any company"}
                    </label>
                  </div>
                )}
                {(form.path === "managerial" || !form.is_freelance) && (
                  <>
                    <CompanyAutocomplete
                      value={form.staff_company_label}
                      onPick={({ id, label }) =>
                        setForm((p) => ({
                          ...p,
                          staff_company_id: id,
                          staff_company_label: label,
                          staff_new_company_name: "",
                        }))
                      }
                      onCreateNew={(text) =>
                        setForm((p) => ({
                          ...p,
                          staff_company_id: "",
                          staff_company_label: `${text} · NEW ENTRY`,
                          staff_new_company_name: text,
                        }))
                      }
                      onClear={() =>
                        setForm((p) => ({
                          ...p,
                          staff_company_id: "",
                          staff_company_label: "",
                          staff_new_company_name: "",
                        }))
                      }
                    />
                    {form.staff_new_company_name && (
                      <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px]"
                           data-testid="ghost-company-banner">
                        <Lock size={13} className="mt-0.5 text-amber-400" />
                        <span className="text-white/85">
                          <span className="font-semibold text-amber-300">Pending verification.</span>{" "}
                          You can finish your profile now. The company account stays locked
                          until a certified corporate representative validates documents.
                        </span>
                      </div>
                    )}
                  </>
                )}
              </Field>
            )}

            {/* ── Personal Section (bottom) ────────────────────── */}
            <div className="pt-3 border-t border-white/10">
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/45 mb-4">Personal Details</div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full Name">
                  <Input data-testid="reg-name" value={form.name} required
                    onChange={(e) => upd("name", e.target.value)}
                    className="bg-black/40 border-white/15 h-11" />
                </Field>
                <Field label="Email">
                  <Input data-testid="reg-email" type="email" value={form.email} required
                    onChange={(e) => upd("email", e.target.value)}
                    className="bg-black/40 border-white/15 h-11" />
                  {orgMatch && (
                    <div className="mt-2 px-3 py-2 rounded-lg border border-[#F5B82E]/40 bg-[#F5B82E]/10 flex items-center gap-2 text-[11px]"
                         data-testid="domain-match-banner">
                      <Building2 size={14} className="text-[#F5B82E]" />
                      <span className="text-white/85">
                        Looks like you work at <span className="text-white font-medium">{orgMatch.brand_name}</span>.
                      </span>
                    </div>
                  )}
                </Field>
                <Field label="Password">
                  <Input data-testid="reg-password" type="password" value={form.password} required minLength={6}
                    onChange={(e) => upd("password", e.target.value)}
                    className="bg-black/40 border-white/15 h-11" />
                </Field>
                <Field label="Phone">
                  <Input data-testid="reg-phone" value={form.phone}
                    onChange={(e) => upd("phone", e.target.value)}
                    className="bg-black/40 border-white/15 h-11" />
                </Field>
              </div>
            </div>

            <Button type="submit" disabled={busy}
              className="btn-themed h-12 w-full rounded-full text-xs uppercase tracking-[0.25em] font-semibold"
              data-testid="register-submit">
              {busy ? "Verifying…" : "Verify my Credentials →"}
            </Button>
          </form>

          <div className="mt-6 text-center text-xs text-white/60">
            Already verified?{" "}
            <Link to="/login" className="text-[#F5B82E] hover:underline" data-testid="goto-login">
              Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-[10px] tracking-[0.25em] uppercase text-white/60">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

// ─── Company autocomplete (multi-kind: PH/Agency/Studio/Hall/Distributor/Exhibitor/Vendor/Investor) ──────
function CompanyAutocomplete({ value, onPick, onCreateNew, onClear }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrap = useRef(null);

  useEffect(() => { if (!value) setQ(""); }, [value]);

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      // Search across ALL org kinds — user picks by name; ghost-org
      // creation later chooses the right kind from the department.
      api.get(`/orgs/search?q=${encodeURIComponent(q)}&limit=8`)
        .then(({ data }) => setResults(data.results || []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  if (value) {
    return (
      <div className="flex items-center gap-2 h-11 px-3 rounded-md border border-white/15 bg-black/40"
           data-testid="reg-company-chip">
        <Building2 size={14} className="text-[#F5B82E]" />
        <span className="text-sm text-white truncate flex-1" title={value}>{value}</span>
        <button type="button" onClick={onClear}
          className="text-[10px] tracking-[0.22em] uppercase text-white/50 hover:text-white"
          data-testid="reg-company-clear">Change</button>
      </div>
    );
  }

  const showEmpty = q.length >= 2 && !loading && results.length === 0;

  return (
    <div className="relative" ref={wrap}>
      <div className="flex items-center gap-2 h-11 px-3 rounded-md border border-white/15 bg-black/40 focus-within:border-[#F5B82E]/60">
        <Search size={14} className="text-white/40" />
        <input
          data-testid="reg-company-search"
          type="text"
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          placeholder="Search agency · studio · production house · cinema hall · distributor…"
          className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-white/35"
          autoComplete="off"
        />
      </div>

      {open && (q.length >= 2 || results.length > 0) && (
        <div className="absolute left-0 right-0 mt-1 z-30 max-h-64 overflow-y-auto rounded-lg border border-white/12 bg-[#0c0c0e] shadow-xl"
             data-testid="reg-company-dropdown">
          {loading && (
            <div className="px-3 py-2 text-[11px] tracking-[0.22em] uppercase text-white/50">Searching…</div>
          )}
          {results.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => { onPick({ id: r.id, label: r.brand_name || r.legal_name }); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-white/5 flex items-center gap-2 border-b border-white/5 last:border-0"
              data-testid={`reg-company-result-${r.id}`}
            >
              <Building2 size={13} className={r.verified ? "text-emerald-400" : "text-white/40"} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{r.brand_name || r.legal_name}</div>
                {r.legal_name && r.legal_name !== r.brand_name && (
                  <div className="text-[10px] text-white/40 truncate">{r.legal_name}</div>
                )}
              </div>
              <span className={`text-[9px] tracking-[0.22em] uppercase ${
                r.status === "PENDING_VERIFICATION" ? "text-amber-400" :
                r.verified ? "text-emerald-400" : "text-white/40"}`}>
                {r.status === "PENDING_VERIFICATION" ? "Pending" : r.verified ? "Verified" : "Unverified"}
              </span>
            </button>
          ))}

          {(showEmpty || (results.length > 0 && q.length >= 2)) && (
            <CreateNewRow
              current={q}
              onCreate={(text) => { onCreateNew(text); setOpen(false); }}
              empty={showEmpty}
            />
          )}
        </div>
      )}
    </div>
  );
}

function CreateNewRow({ current, onCreate, empty }) {
  const [text, setText] = useState(current);
  useEffect(() => { setText(current); }, [current]);
  const commit = () => {
    const t = text.trim();
    if (t.length < 2) return;
    onCreate(t);
  };
  return (
    <div className="px-3 py-2 border-t border-white/10 bg-black/40" data-testid="reg-company-create-row">
      <div className={`text-[10px] tracking-[0.22em] uppercase mb-1.5 ${
        empty ? "text-amber-300" : "text-white/50"}`}>
        {empty ? "Company not listed? Create new entry" : "Can't find your company? Add it →"}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
          placeholder="Type the exact company name and press Add"
          className="flex-1 bg-black/60 border border-white/15 rounded-md h-9 px-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-[#F5B82E]/60"
          data-testid="reg-company-new-input"
        />
        <button
          type="button"
          onClick={commit}
          className="h-9 px-3 rounded-md bg-[#F5B82E]/90 hover:bg-[#F5B82E] text-black text-[11px] uppercase tracking-[0.22em] font-semibold inline-flex items-center gap-1"
          data-testid="reg-company-new-add"
        >
          <Plus size={12} /> Add
        </button>
      </div>
    </div>
  );
}
